"use client";
import { CreateSessionCookie, GetSessionCookie } from "@/services/Cookies/FirebaseSession";
import { User as ServerUser } from "@/components/Classes/User";
import type { User } from "firebase/auth";
import React, { useContext, useEffect, useState, useMemo, useCallback } from "react";
import { getCurrentSubscription } from "@/services/APIs/AIUsageService";
import { UserSubscriptionDto } from "@/types/AIUsageTypes";

/**
 * Interface defining the authentication context shape and available methods.
 */
interface AuthContextType {
	/** Indicates if the authentication state is loading */
	loading: boolean;

	/** Current authenticated user or null if not logged in */
	currentUser: User | null;

	/** Server-side session user data */
	sessionUser: ServerUser | null;

	/** User's subscription data */
	subscription: UserSubscriptionDto | null;

	/** Indicates if the subscription is loading */
	subscriptionLoading: boolean;

	/** Role of the current user (e.g., "user", "admin") */
	userRole: string;

	/** Token claims for the current user */
	claims: Record<string, any> | null;

	/**
	 * Authenticates a user with email and password
	 * @param email - User's email address
	 * @param password - User's password
	 * @returns Promise resolving to authentication result
	 */
	login: (email: string, password: string) => Promise<any>;

	/**
	 * Creates a new user account with email and password
	 * @param email - User's email address
	 * @param password - User's password
	 * @returns Promise resolving to signup result
	 */
	signup: (email: string, password: string) => Promise<any>;

	/**
	 * Authenticates a user with Google OAuth
	 * @param token - OAuth token
	 * @param isAccessToken - Whether the token is an access token (true) or ID token (false)
	 * @returns Promise resolving to authentication result
	 */
	googleSignIn: (token: string, isAccessToken: boolean) => Promise<any>;

	/** Signs out the current user */
	logout: () => Promise<void>;

	/**
	 * Sends password reset email
	 * @param email - User's email address
	 */
	resetPassword: (email: string) => Promise<void>;

	/**
	 * Updates the email address for the current user
	 * @param email - New email address
	 */
	updateEmail: (email: string) => Promise<void>;

	/**
	 * Updates the password for the current user
	 * @param password - New password
	 */
	updatePassword: (password: string) => Promise<void>;

	/**
	 * Sets up reCAPTCHA verification for phone number
	 * @param number - Phone number to verify
	 */
	setUpRecaptcha: (number: string) => Promise<void>;

	/**
	 * Refreshes the current user's session cookie
	 * @returns {Promise<boolean>} - Whether the cookie was successfully refreshed
	 */
	refreshCookie: () => Promise<boolean>;

	/**
	 * Refreshes the user's subscription data
	 * @returns {Promise<void>}
	 */
	refreshSubscription: () => Promise<void>;

	/**
	 * Checks if the user is logged in by verifying sessionUser.uid
	 * @returns {boolean} - True if user is logged in, false otherwise
	 */
	isUserLoggedIn: () => boolean;
}

// Default auth context with empty implementations
const AuthContext = React.createContext<AuthContextType>({
	loading: true,
	currentUser: null,
	sessionUser: null,
	subscription: null,
	subscriptionLoading: true,
	userRole: "",
	claims: null,
	login: async () => {},
	signup: async () => {},
	googleSignIn: async () => {},
	logout: async () => {},
	resetPassword: async () => {},
	updateEmail: async () => {},
	updatePassword: async () => {},
	setUpRecaptcha: async () => {},
	refreshCookie: async () => false,
	refreshSubscription: async () => {},
	isUserLoggedIn: () => false,
});

/**
 * Custom hook to access authentication context
 * @returns AuthContextType object containing auth state and methods
 */
export function useAuth() {
	const context = useContext(AuthContext);

	// If context is not available, return a safe default
	if (!context) {
		console.warn("useAuth must be used within an AuthProvider. Returning default context.");
		return {
			sessionUser: null,
			currentUser: null,
			subscription: null,
			subscriptionLoading: true,
			userRole: "user",
			claims: null,
			loading: false,
			signup: async () => ({ success: false }),
			login: async () => ({ success: false }),
			googleSignIn: async () => ({ success: false }),
			logout: async () => {},
			resetPassword: async () => {},
			updateEmail: async () => {},
			updatePassword: async () => {},
			setUpRecaptcha: async () => {},
			refreshCookie: async () => false,
			refreshSubscription: async () => {},
			isUserLoggedIn: () => false,
		};
	}

	return context;
}

/**
 * Authentication provider component that wraps the application
 * and provides authentication state and methods to children
 *
 * @param children - Child components that will have access to auth context
 * @param sessionUser - Server-side session user data passed from root layout
 */
export function AuthProvider({
	children,
	sessionUser: initialSessionUser = null,
	enableFirebase = true,
}: {
	children: React.ReactNode;
	sessionUser?: ServerUser | null;
	/** Public routes can opt out of Firebase until a user explicitly visits an auth flow. */
	enableFirebase?: boolean;
}) {
	const [currentUser, setCurrentUser] = useState<User | null>(null);
	const [sessionUser, setSessionUser] = useState<ServerUser | null>(initialSessionUser);
	const [subscription, setSubscription] = useState<UserSubscriptionDto | null>(null);
	const [subscriptionLoading, setSubscriptionLoading] = useState(enableFirebase);
	const [userRole, setUserRole] = useState("user");
	const [claims, setClaims] = useState<Record<string, any> | null>(null);
	const [loading, setLoading] = useState(enableFirebase);

	// Update sessionUser when props change
	useEffect(() => {
		setSessionUser(initialSessionUser);
	}, [initialSessionUser]);

	/**
	 * Creates a new user account with email and password
	 * @param email - User's email address
	 * @param password - User's password
	 * @returns Promise resolving to signup result
	 */
	const signup = useCallback(async (email: string, password: string) => {
		const server = await import("./ServerAuth");
		return server.signup(email, password);
	}, []);

	const login = useCallback(async (email: string, password: string) => {
		const server = await import("./ServerAuth");
		return server.login(email, password);
	}, []);

	const googleSignIn = useCallback(async (token: string, isAccessToken: boolean) => {
		const server = await import("./ServerAuth");
		return server.googleSignIn(token, isAccessToken);
	}, []);

	/**
	 * Signs out the current user and resets auth state
	 */
	const logout = useCallback(async () => {
		setCurrentUser(null);
		setSessionUser(null);
		setSubscription(null);
		setClaims(null);
		setUserRole("user");
		const server = await import("./ServerAuth");
		await server.logout();
	}, []);

	/**
	 * Refreshes the user's subscription data
	 */
	const refreshSubscription = useCallback(async () => {
		if (!currentUser) {
			setSubscription(null);
			return;
		}

		setSubscriptionLoading(true);
		try {
			const subscriptionData = await getCurrentSubscription();
			setSubscription(subscriptionData);
		} catch (error) {
			console.error("Failed to fetch subscription:", error);
			setSubscription(null);
		} finally {
			setSubscriptionLoading(false);
		}
	}, [currentUser]);

	/**
	 * Sends password reset email
	 * @param email - User's email address
	 */
	const resetPassword = useCallback(async (email: string) => {
		const server = await import("./ServerAuth");
		return server.resetPassword(email);
	}, []);

	/**
	 * Updates the email address for the current user
	 * @param email - New email address
	 */
	const updateEmail = useCallback(async (email: string) => {
		const server = await import("./ServerAuth");
		return server.updateEmail(email, currentUser);
	}, [currentUser]);

	/**
	 * Updates the password for the current user
	 * @param password - New password
	 */
	const updatePassword = useCallback(async (password: string) => {
		const server = await import("./ServerAuth");
		return server.updatePassword(password, currentUser);
	}, [currentUser]);

	/**
	 * Sets up reCAPTCHA verification for phone number
	 * @param number - Phone number to verify
	 * @remarks Currently a placeholder implementation
	 */
	async function setUpRecaptcha(number: string) {
		// const recaptchaVerifier = new firebase.app.auth().RecaptchaVerifier(
		// 'recaptcha-container',
		// {}
		// );
		// recaptchaVerifier.render()
		// return app.auth().currentUser.linkWithPhoneNumber(number, recaptchaVerifier)
		return;
	}

	/**
	 * Refreshes the current user's session cookie
	 * @returns {Promise<boolean>} - Whether the cookie was successfully refreshed
	 */
	const refreshCookie = useCallback(async () => {
		try {
			if (!currentUser) return false;
			const idToken = await currentUser.getIdToken(true);
			await CreateSessionCookie(idToken);
			
			// Update local claims state
			const idTokenResult = await currentUser.getIdTokenResult(true);
			setClaims(idTokenResult.claims);
			setUserRole((idTokenResult.claims.role as string) || "user");
		} catch (error) {
			console.error("Error refreshing session cookie:", error);
			return false;
		}
		return true;
	}, [currentUser]);

	/**
	 * Checks if the user is logged in by verifying sessionUser.uid
	 * @returns {boolean} - True if user is logged in, false otherwise
	 */
	const isUserLoggedIn = useCallback(() => {
		return sessionUser?.uid ? true : false;
	}, [sessionUser]);

	// Set up auth state listener on component mount
	useEffect(() => {
		if (!enableFirebase) {
			setLoading(false);
			setSubscriptionLoading(false);
			return;
		}

		let unsubscribe: (() => void) | undefined;
		let cancelled = false;

		void (async () => {
			const { app } = await import("./firebase_config");
			if (cancelled) return;

			unsubscribe = app.auth().onAuthStateChanged(async (user) => {
			setCurrentUser(user);
			if (user !== null) {
				// Firestore is not needed until a user has authenticated.
				const { UpdateLogin } = await import("./Firestore");
				void UpdateLogin(user);
				const claims = await user.getIdTokenResult(true);
				setClaims(claims.claims);
				setUserRole((claims.claims.role as string) || "user");
			}

			const sessionCookie = await GetSessionCookie();
			if (user !== null && sessionCookie === null) {
				// console.log(user, sessionCookie, "No session cookie found");
				// await CreateSessionCookie(await user.getIdToken());
				// return await logout();
			}

			// Fetch subscription data when user logs in
			setSubscriptionLoading(true);
			try {
				const subscriptionData = await getCurrentSubscription();
				setSubscription(subscriptionData);
			} catch (error) {
				console.error("Failed to fetch subscription:", error);
				setSubscription(null);
			} finally {
				setSubscriptionLoading(false);
			}

			setLoading(false);
			});
		})();

		return () => {
			cancelled = true;
			unsubscribe?.();
		};
	}, [enableFirebase]);

	const value = useMemo(() => ({
		loading,
		currentUser,
		sessionUser,
		subscription,
		subscriptionLoading,
		userRole,
		claims,
		login,
		signup,
		googleSignIn,
		logout,
		resetPassword,
		updateEmail,
		updatePassword,
		setUpRecaptcha,
		refreshCookie,
		refreshSubscription,
		isUserLoggedIn,
	}), [
		loading,
		currentUser,
		sessionUser,
		subscription,
		subscriptionLoading,
		userRole,
		claims,
		login,
		signup,
		googleSignIn,
		logout,
		resetPassword,
		updateEmail,
		updatePassword,
		setUpRecaptcha,
		refreshCookie,
		refreshSubscription,
		isUserLoggedIn,
	]);

	// if (loading) return <LoadingSpinner />;

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
